import React, { forwardRef } from 'react';
import { AlertCircle, Check, Loader2 } from 'lucide-react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    hint?: string;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    containerClassName?: string;

    // Premium validation states
    validationState?: 'idle' | 'validating' | 'success' | 'error';
    showValidation?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(({
    label,
    error,
    hint,
    leftIcon,
    rightIcon,
    className = '',
    containerClassName = '',
    id,
    validationState = 'idle',
    showValidation = false,
    ...props
}, ref) => {
    // Generate random ID if none provided
    const inputId = id || props.name || `input-${Math.random().toString(36).substr(2, 9)}`;

    // Determine actual validation state
    const actualState = error ? 'error' : validationState;
    const showValidationIcon = showValidation && (actualState === 'success' || actualState === 'error' || actualState === 'validating');

    return (
        <div className={`w-full space-y-1.5 font-sans ${containerClassName}`}>
            {label && (
                <label
                    htmlFor={inputId}
                    className="block text-sm font-semibold text-gray-700 mb-1.5"
                >
                    {label}
                    {props.required && <span className="text-red-500 ml-1">*</span>}
                </label>
            )}

            <div className="relative group">
                {leftIcon && (
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-[var(--color-primary)] transition-colors duration-200 pointer-events-none z-10">
                        {leftIcon}
                    </div>
                )}

                <input
                    ref={ref}
                    id={inputId}
                    className={`
            w-full bg-white border rounded-xl text-gray-900 placeholder-gray-400
            transition-all duration-300 ease-smooth
            focus:outline-none focus:ring-2 focus:ring-opacity-50
            disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed
            ${leftIcon ? 'pl-10' : 'pl-4'}
            ${rightIcon || showValidationIcon ? 'pr-10' : 'pr-4'}
            ${props.type === "color" ? "h-[45px] p-1" : "py-2.5"} 
            min-h-[44px] /* Touch target size for mobile */
            ${actualState === 'error'
                            ? 'border-red-300 focus:border-red-500 focus:ring-red-200 bg-red-50/30'
                            : actualState === 'success'
                                ? 'border-emerald-300 focus:border-emerald-500 focus:ring-emerald-200 bg-emerald-50/20'
                                : 'border-gray-200 hover:border-gray-300 focus:border-[var(--color-primary)] focus:ring-[var(--color-teal)]'
                        }
            ${className}
          `}
                    {...props}
                />

                {/* Validation Icons with Animations */}
                {showValidationIcon && (
                    <div className={`absolute right-3 top-1/2 -translate-y-1/2 transition-all duration-300 ${actualState === 'validating' ? 'animate-spin' : 'animate-in zoom-in-50 fade-in'
                        }`}>
                        {actualState === 'validating' && (
                            <Loader2 className="w-5 h-5 text-gray-400" />
                        )}
                        {actualState === 'success' && (
                            <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center animate-in zoom-in-90 fade-in duration-200">
                                <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                            </div>
                        )}
                        {actualState === 'error' && (
                            <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center animate-in zoom-in-90 fade-in duration-200">
                                <AlertCircle className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                            </div>
                        )}
                    </div>
                )}

                {/* Custom Right Icon (if no validation) */}
                {!showValidationIcon && rightIcon && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                        {rightIcon}
                    </div>
                )}
            </div>

            {/* Error Message with Animation */}
            {error && (
                <p className="text-sm text-red-600 mt-1.5 flex items-center gap-1.5 animate-in slide-in-from-top-1 fade-in duration-200">
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{error}</span>
                </p>
            )}

            {/* Hint Text */}
            {!error && hint && (
                <p className="text-sm text-gray-500 mt-1">
                    {hint}
                </p>
            )}
        </div>
    );
});

Input.displayName = 'Input';
